import { evalutateTestService } from '@/api/services/testService'
import { QuestionAnswer } from '@/api/services/types/resultsTypes'
import { IdWrapper, TestDetailsResponse } from '@/api/services/types/testTypes'
import { createContext, ReactNode, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

interface ModuleResponse {
  moduleId: IdWrapper
  questionAnswers: QuestionAnswer[]
}

export interface TestResponse {
  testId: IdWrapper
  testDataJson: string
  moduleAnswers: ModuleResponse[]
}

interface TestResponseContextType {
  testResponse: TestResponse | null
  addQuestionResponse: (moduleId: IdWrapper, response: QuestionAnswer) => Promise<void>
  updateQuestionResponse: (
    moduleId: IdWrapper,
    questionId: IdWrapper,
    response: Partial<QuestionAnswer>
  ) => void
  getQuestionResponse: (
    moduleId: IdWrapper,
    questionId: IdWrapper
  ) => QuestionAnswer | undefined
  clearTestResponse: () => void
}

export const TestResponseContext = createContext<TestResponseContextType | undefined>(
  undefined
)

export function TestResponseProvider({
  id,
  data,
  stringifyJson,
  children
}: {
  id: string | null
  data: TestDetailsResponse | null
  stringifyJson: string
  children: ReactNode
}) {
  const [testResponse, setTestResponse] = useState<TestResponse | null>(
    id
      ? {
          testId: id,
          testDataJson: stringifyJson,
          moduleAnswers: []
        }
      : null
  )
  const [isEvaluating, setIsEvaluating] = useState(false)
  const navigate = useNavigate()

  useEffect(() => {
    const checkAndEvaluate = async () => {
      if (data && testResponse && testResponse.moduleAnswers.length > 0) {
        const lastModule =
          testResponse.moduleAnswers[testResponse.moduleAnswers.length - 1]
        const testLastModule = data.modules[data.modules.length - 1]

        if (lastModule.moduleId === testLastModule.id) {
          const lastQuestion =
            lastModule.questionAnswers[lastModule.questionAnswers.length - 1]
          const testLastQuestion = testLastModule.randomizeOrder
            ? testLastModule.randomizedAndCountedQuestions[
                testLastModule.randomizedAndCountedQuestions.length - 1
              ]
            : testLastModule.allQuestions[testLastModule.allQuestions.length - 1]
          if (
            lastQuestion.questionId === testLastQuestion.questionId ||
            lastQuestion.questionId === testLastQuestion.id
          ) {
            await evaluateTest(testResponse)
          }
        }
      }
    }

    checkAndEvaluate().catch((error) => {
      console.error('Błąd podczas ewaluacji testu:', error)
    })
  }, [testResponse, data])

  const evaluateTest = async (resp: TestResponse) => {
    if (!resp || isEvaluating) {
      return
    }

    setIsEvaluating(true)

    try {
      const response = await evalutateTestService(resp)

      if (response.success) {
        const id = response.data.id
        navigate(`${id}`)
      } else {
        console.error('TestResponseContext: Błąd podczas ewaluacji testu', response)
      }
    } catch (error) {
      console.error('TestResponseContext: Błąd podczas ewaluacji testu', error)
    } finally {
      setIsEvaluating(false)
    }
  }

  const addQuestionResponse = (moduleId: IdWrapper, response: QuestionAnswer) => {
    return new Promise<void>((resolve) => {
      setTestResponse((prev) => {
        if (!prev) {
          resolve()
          return null
        }

        const moduleIndex = prev.moduleAnswers.findIndex((m) => m.moduleId === moduleId)
        console.log('moduleIndex ', moduleIndex)
        if (moduleIndex === -1) {
          resolve()
          return {
            ...prev,
            moduleAnswers: [
              ...prev.moduleAnswers,
              {
                moduleId,
                questionAnswers: [response]
              }
            ]
          }
        }

        const updatedModuleResponses = [...prev.moduleAnswers]
        const module = { ...updatedModuleResponses[moduleIndex] }

        const existingResponseIndex = module.questionAnswers.findIndex(
          (r) => r.questionId === response.questionId
        )

        if (existingResponseIndex === -1) {
          module.questionAnswers = [...module.questionAnswers, response]
        } else {
          const existingAnswer = module.questionAnswers[existingResponseIndex]
          module.questionAnswers[existingResponseIndex] = {
            ...existingAnswer,
            ...response,
            attentionTaskAnswer:
              response.attentionTaskAnswer || existingAnswer.attentionTaskAnswer,
            balloonTaskAnswer:
              response.balloonTaskAnswer || existingAnswer.balloonTaskAnswer,
            cognitiveFlexibilityAnswer:
              response.cognitiveFlexibilityAnswer ||
              existingAnswer.cognitiveFlexibilityAnswer,
            goNoGoTaskAnswer:
              response.goNoGoTaskAnswer || existingAnswer.goNoGoTaskAnswer,
            iowaGamblingTaskAnswer:
              response.iowaGamblingTaskAnswer || existingAnswer.iowaGamblingTaskAnswer,
            likertScaleAnswer:
              response.likertScaleAnswer || existingAnswer.likertScaleAnswer,
            memoryTaskAnswer:
              response.memoryTaskAnswer || existingAnswer.memoryTaskAnswer,
            multipleChoiceAnswer:
              response.multipleChoiceAnswer || existingAnswer.multipleChoiceAnswer,
            openQuestionAnswer:
              response.openQuestionAnswer || existingAnswer.openQuestionAnswer,
            imageMatchingAnswer:
              response.imageMatchingAnswer || existingAnswer.imageMatchingAnswer,
            sequencingTaskAnswer:
              response.sequencingTaskAnswer || existingAnswer.sequencingTaskAnswer,
            logicalOddOneOutTaskAnswer:
              response.logicalOddOneOutTaskAnswer ||
              existingAnswer.logicalOddOneOutTaskAnswer
          }
        }

        updatedModuleResponses[moduleIndex] = module
        const value = {
          ...prev,
          moduleAnswers: updatedModuleResponses
        }

        resolve()
        return value
      })
    })
  }

  const updateQuestionResponse = (
    moduleId: IdWrapper,
    questionId: IdWrapper,
    response: Partial<QuestionAnswer>
  ) => {
    setTestResponse((prev) => {
      if (!prev) return null

      const moduleIndex = prev.moduleAnswers.findIndex((m) => m.moduleId === moduleId)
      if (moduleIndex === -1) return prev

      const updatedModuleResponses = [...prev.moduleAnswers]
      const module = { ...updatedModuleResponses[moduleIndex] }

      const responseIndex = module.questionAnswers.findIndex(
        (r) => r.questionId === questionId
      )

      if (responseIndex === -1) return prev

      module.questionAnswers[responseIndex] = {
        ...module.questionAnswers[responseIndex],
        ...response
      }

      updatedModuleResponses[moduleIndex] = module

      return {
        ...prev,
        moduleAnswers: updatedModuleResponses
      }
    })
  }

  const getQuestionResponse = (moduleId: IdWrapper, questionId: IdWrapper) => {
    if (!testResponse) return undefined

    const module = testResponse.moduleAnswers.find((m) => m.moduleId === moduleId)
    if (!module) return undefined

    return module.questionAnswers.find((r) => r.questionId === questionId)
  }

  const clearTestResponse = () => {
    setTestResponse(null)
  }

  return (
    <TestResponseContext.Provider
      value={{
        testResponse,
        addQuestionResponse,
        updateQuestionResponse,
        getQuestionResponse,
        clearTestResponse
      }}>
      {children}
    </TestResponseContext.Provider>
  )
}
